import { useEffect, useState } from "react";
import { Link } from "wouter";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import { ArrowRight, Clock, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PowloLayout from "@/components/powlo/PowloLayout";
import { apiMutation, ApiError } from "@/api/client";
import { COMPANY_ADDRESS, LEGAL_NAME, SUPPORT_EMAIL } from "@/constants/company";
import SupportPhoneLink from "@/components/SupportPhoneLink";

const SUBJECT_OPTIONS = [
  "Solar quote enquiry",
  "Existing project question",
  "Installer partnership",
  "Technical support",
  "Billing / payments",
  "Complaint",
  "Other",
] as const;

type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const initialForm: FormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const fieldClassName =
  "w-full min-h-[52px] rounded-xl border-2 border-border bg-white px-4 text-base text-ink placeholder:text-[#6b6e76] focus:border-indigo focus:outline-none";

export default function Contact() {
  useEffect(() => {
    scrollToPageTop();
  }, []);

  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      await apiMutation<{ success: boolean }>("/api/contact", form);
      setSubmitted(true);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : `Something went wrong. Please email us directly at ${SUPPORT_EMAIL}`;
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PowloLayout>
      <Seo
        title="Contact"
        description="Have a question about solar, your quote, or the platform? We're here to help."
        path="/contact"
      />
      <section className="flex flex-col gap-4 px-5 pb-8 pt-14 sm:px-8 lg:px-20">
        <div className="text-sm font-bold tracking-widest text-indigo">GET IN TOUCH</div>
        <h1 className="m-0 max-w-3xl font-display text-4xl font-bold leading-tight tracking-tight text-ink sm:text-6xl">
          Contact us
        </h1>
        <p className="m-0 max-w-2xl text-lg leading-relaxed text-[#2A2A33]">
          Have a question about solar, your quote, or the platform? We&apos;re here to help.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-8 px-5 pb-24 sm:px-8 lg:grid-cols-2 lg:gap-12 lg:px-20">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-5 rounded-2xl border border-border bg-white p-6">
            <ContactRow icon={Mail} title="Email us">
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm font-semibold text-indigo hover:underline">
                {SUPPORT_EMAIL}
              </a>
              <p className="m-0 mt-1 text-sm text-[#2A2A33]">We respond within 1 business day</p>
            </ContactRow>
            <ContactRow icon={Phone} title="Call us">
              <SupportPhoneLink
                showIcon={false}
                className="min-h-[44px] text-sm font-semibold text-indigo hover:underline"
              />
            </ContactRow>
            <ContactRow icon={Clock} title="Support hours">
              <p className="m-0 text-base text-[#2A2A33]">Monday – Friday, 9am – 6pm GMT</p>
              <p className="m-0 mt-1 text-sm text-[#2A2A33]">Excluding UK public holidays</p>
            </ContactRow>
            <ContactRow icon={MapPin} title="Registered address">
              <p className="m-0 text-base leading-relaxed text-[#2A2A33]">
                {LEGAL_NAME}
                <br />
                {COMPANY_ADDRESS.line1}
                <br />
                {COMPANY_ADDRESS.city}, {COMPANY_ADDRESS.postcode}
              </p>
            </ContactRow>
          </div>

          <div>
            <p className="mb-3 text-sm font-bold tracking-widest text-indigo">QUICK LINKS</p>
            <div className="flex flex-col gap-2">
              {[
                { href: "/faq", label: "Frequently asked questions" },
                { href: "/quote", label: "Get a free solar quote" },
                { href: "/installer/apply", label: "Apply as an installer" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="group flex min-h-[32px] items-center gap-2 text-sm font-semibold text-ink no-underline hover:text-indigo"
                >
                  <span className="text-indigo transition-transform group-hover:translate-x-0.5">→</span>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div>
          {submitted ? (
            <div className="rounded-2xl border border-border bg-white p-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-lime">
                <Mail className="h-7 w-7" aria-hidden />
              </div>
              <h2 className="m-0 mb-2 font-display text-xl font-bold tracking-tight">Message sent</h2>
              <p className="m-0 text-base leading-relaxed text-[#2A2A33]">
                Thanks for reaching out. We&apos;ll get back to you at{" "}
                <strong className="text-ink">{form.email}</strong> within 1 business day.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setForm(initialForm);
                }}
                className="mt-6 text-sm font-semibold text-indigo hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-2xl border border-border bg-white p-6">
              <h2 className="m-0 font-display text-xl font-bold tracking-tight">Send us a message</h2>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-ink">
                  Name <span className="text-indigo">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Your full name"
                  className={fieldClassName}
                  required
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-ink">
                  Email <span className="text-indigo">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update({ email: e.target.value })}
                  placeholder="you@example.com"
                  className={fieldClassName}
                  required
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-ink">Subject</label>
                <select
                  value={form.subject}
                  onChange={(e) => update({ subject: e.target.value })}
                  className={fieldClassName}
                >
                  <option value="">Select a topic…</option>
                  {SUBJECT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-ink">
                  Message <span className="text-indigo">*</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => update({ message: e.target.value })}
                  placeholder="How can we help?"
                  rows={5}
                  className={`${fieldClassName} min-h-[140px] resize-none py-3`}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send message"}
                {!submitting && <ArrowRight className="h-4 w-4" aria-hidden />}
              </button>
              <p className="m-0 text-center text-sm text-[#2A2A33]">
                Or email us directly at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-indigo hover:underline">
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </form>
          )}
        </div>
      </section>
    </PowloLayout>
  );
}

function ContactRow({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Mail;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-navy text-lime">
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div>
        <p className="m-0 mb-1 text-sm font-bold text-ink">{title}</p>
        {children}
      </div>
    </div>
  );
}
